import { Component, ChangeDetectionStrategy, ViewEncapsulation, viewChild, signal, DestroyRef, inject, OnInit, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

import { WindowService } from 'src/shared/providers/window.service';
import { SharedModule } from 'src/shared/shared.module';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { ExpandableChatComponent, ADD_BUTTON_ICON_URI } from 'src/shared/components/expandable-chat/expandable-chat.component';

const CHATBUBBLES_SVG = `<svg clip-rule="evenodd" fill-rule="evenodd" height="512" stroke-linejoin="round" stroke-miterlimit="2" viewBox="0 0 32 32" width="512" xmlns="http://www.w3.org/2000/svg"><g transform="translate(-333 -74)"><g id="Solid"><path fill="white" d="m349 76c-7.727 0-14 6.273-14 14s6.273 14 14 14 14-6.273 14-14-6.273-14-14-14zm-8 11c-1.656 0-3 1.344-3 3s1.344 3 3 3 3-1.344 3-3-1.344-3-3-3zm8 0c-1.656 0-3 1.344-3 3s1.344 3 3 3 3-1.344 3-3-1.344-3-3-3zm8 0c-1.656 0-3 1.344-3 3s1.344 3 3 3 3-1.344 3-3-1.344-3-3-3z"/></g></g></svg>`;
const CHATBUBBLES_ICON_URI = `data:image/svg+xml,${encodeURIComponent(CHATBUBBLES_SVG)}`;

const CREATE_SVG = `<svg width="512" height="512" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M459.939 53.25C458.478 51.6445 456.706 50.3518 454.732 49.4502C452.757 48.5486 450.619 48.0568 448.449 48.0044C446.279 47.9521 444.12 48.3403 442.104 49.1457C440.088 49.951 438.256 51.1568 436.719 52.69L424.349 65C422.85 66.5002 422.008 68.5342 422.008 70.655C422.008 72.7759 422.85 74.8099 424.349 76.31L435.689 87.63C436.433 88.3769 437.316 88.9695 438.289 89.3738C439.262 89.7782 440.306 89.9864 441.359 89.9864C442.413 89.9864 443.456 89.7782 444.429 89.3738C445.402 88.9695 446.286 88.3769 447.029 87.63L459.089 75.63C465.189 69.54 465.759 59.62 459.939 53.25ZM399.339 90L218.819 270.2C217.725 271.29 216.929 272.644 216.509 274.13L208.159 299C207.959 299.675 207.945 300.391 208.118 301.073C208.291 301.756 208.646 302.378 209.143 302.876C209.641 303.374 210.264 303.728 210.946 303.901C211.628 304.074 212.344 304.06 213.019 303.86L237.869 295.51C239.356 295.09 240.709 294.295 241.799 293.2L421.999 112.66C423.666 110.975 424.602 108.7 424.602 106.33C424.602 103.96 423.666 101.685 421.999 100L412.049 90C410.362 88.3177 408.077 87.373 405.694 87.373C403.312 87.373 401.026 88.3177 399.339 90V90Z" fill="white"/><path d="M386.34 193.66L264.45 315.79C259.739 320.513 253.949 324.017 247.58 326L221.68 334.67C215.533 336.406 209.035 336.471 202.854 334.86C196.674 333.248 191.035 330.018 186.519 325.501C182.002 320.985 178.772 315.346 177.16 309.165C175.549 302.985 175.614 296.487 177.35 290.34L186.02 264.44C187.997 258.073 191.495 252.283 196.21 247.57L318.34 125.66C319.459 124.542 320.222 123.117 320.532 121.565C320.841 120.013 320.684 118.404 320.079 116.942C319.474 115.48 318.449 114.23 317.134 113.35C315.819 112.471 314.272 112.001 312.69 112H104C89.1479 112 74.9041 117.9 64.402 128.402C53.9 138.904 48 153.148 48 168V408C48 422.852 53.9 437.096 64.402 447.598C74.9041 458.1 89.1479 464 104 464H344C358.852 464 373.096 458.1 383.598 447.598C394.1 437.096 400 422.852 400 408V199.31C399.999 197.728 399.529 196.181 398.65 194.866C397.77 193.551 396.52 192.526 395.058 191.921C393.596 191.316 391.987 191.159 390.435 191.468C388.883 191.778 387.458 192.541 386.34 193.66Z" fill="white"/></svg>`;
const CREATE_ICON_URI = `data:image/svg+xml,${encodeURIComponent(CREATE_SVG)}`;

@Component({
  selector: 'app-chat',
  templateUrl: './chat.page.html',
  styleUrls: ['./chat.page.scss'],
  standalone: true,
  imports: [CommonModule, SharedModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ChatPage implements OnInit {
  readonly chat = viewChild(ExpandableChatComponent);
  readonly historyPanelOpen = signal(false);
  readonly isKeyboardVisible = signal(false);
  readonly addButtonIconUri = ADD_BUTTON_ICON_URI;
  readonly chatbubblesIconUri = CHATBUBBLES_ICON_URI;
  readonly createIconUri = CREATE_ICON_URI;

  private destroyRef = inject(DestroyRef);

  constructor(public windowService: WindowService) {
    effect(() => {
      const isLoading = this.chat()?.isLoading() ?? false;
      if (!this.windowService.isDesktopRes) {
        const bars = document.querySelectorAll('.js-tidy-loading') as NodeListOf<HTMLElement>;
        bars.forEach(bar => bar.style.display = isLoading ? 'block' : 'none');
      }
    });

    this.destroyRef.onDestroy(() => {
      if (!this.windowService.isDesktopRes) {
        const bars = document.querySelectorAll('.js-tidy-loading') as NodeListOf<HTMLElement>;
        bars.forEach(bar => bar.style.display = 'none');
      }
    });
  }

  ngOnInit(): void {
    if (Capacitor.isNativePlatform()) {
      Keyboard.addListener('keyboardWillShow', () => {
        this.isKeyboardVisible.set(true);
      });
      Keyboard.addListener('keyboardDidShow', () => {
        setTimeout(() => this.chat()?.scrollToBottom(), 100);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        this.isKeyboardVisible.set(false);
      });
      this.destroyRef.onDestroy(() => {
        Keyboard.removeAllListeners();
      });
    }
  }

  onMenuClick(): void {
    this.historyPanelOpen.update(v => !v);
  }

  onNewChat(): void {
    this.chat()?.newChat();
  }

  onHistoryPanelToggle(open: boolean): void {
    this.historyPanelOpen.set(open);
  }

  onHistorySessionSelect(sessionValue: string): void {
    this.chat()?.onHistorySessionSelect(sessionValue);
    this.historyPanelOpen.set(false);
  }

  onHistoryNewChat(): void {
    this.chat()?.onHistoryNewChat();
    this.historyPanelOpen.set(false);
  }
}
